import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

// #region Variables
const LoadAdditionalResourcesVariablesSchema = z.object({
  debtorId: z.number(),
  actionType: z.string(),
});

export type LoadAdditionalResourcesVariables = z.infer<
  typeof LoadAdditionalResourcesVariablesSchema
>;
// #endregion Variables

// #region Response
const AdditionalResourceInvoiceSchema = z.object({
  id: z.coerce.number(),
  reference: z.string(),
  issueDate: z.string().transform(s => dayjs(s)),
  dueDate: z.string().transform(s => dayjs(s)),
  daysLate: z.coerce.number(),
  currency: z.string(),
  localizedMoneyObject: z.object({
    totalTvac: z.coerce.number(),
    remainingBalance: z.coerce.number(),
  }),
});

export type AdditionalResourceInvoice = z.infer<typeof AdditionalResourceInvoiceSchema>;

const AdditionalResourceCreditNoteSchema = z.object({
  id: z.coerce.number(),
  reference: z.string(),
  issueDate: z.string().transform(s => dayjs(s)),
  totalTvac: z.coerce.number(),
  currency: z.string(),
  localizedMoneyObject: z.object({ remainingBalance: z.coerce.number() }),
});

const AdditionalResourcePaymentSchema = z.object({
  id: z.coerce.number(),
  type: z.union([z.literal('accounting'), z.literal('third_party')]),
  date: z.string().transform(s => dayjs(s)),
  currency: z.string(),
  remainingBalance: z.coerce.number(),
  localizedMoneyObject: z.object({ amount: z.coerce.number() }),
});

const AdditionalResourcesSchema = z.object({
  invoices: z.array(AdditionalResourceInvoiceSchema),
  creditNotes: z.array(AdditionalResourceCreditNoteSchema),
  payments: z.array(AdditionalResourcePaymentSchema),
});

export type AdditionalResources = z.infer<typeof AdditionalResourcesSchema>;
// #endregion Response

const PLACEHOLDER: AdditionalResources = { invoices: [], creditNotes: [], payments: [] };

// MARK: Load function
async function loadAdditionalResourcesQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: LoadAdditionalResourcesVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(`debtors/${variables.debtorId}/additional_resources`, {
    params: { action_type: variables.actionType },
  });

  const parseResult = AdditionalResourcesSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'additionalResources');
    throw new SilentError();
  }

  return parseResult.data;
}

// MARK: Hook
export function useLoadAdditionalResources(
  variables: LoadAdditionalResourcesVariables,
  options: QueryOptions<AdditionalResources> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<AdditionalResources, ApiError>({
    queryKey: [
      'additionalResources',
      { debtorId: variables.debtorId, actionType: variables.actionType },
    ],
    queryFn: () => loadAdditionalResourcesQueryFn(axiosInstance, variables),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return PLACEHOLDER;
    },
    ...options,
  });

  return addResourceNameToQueryResult<
    AdditionalResources,
    ApiError,
    'additionalResources',
    'PreventUndefined'
  >('additionalResources', queryResult, PLACEHOLDER);
}
